import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle2, KeyRound, RefreshCw, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { contactInfo } from '../data/services';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step 1: Login Form
function LoginForm({ onForgotPassword }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      const serverMsg = err.response?.data?.detail;
      setError(serverMsg || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
            <AlertCircle size={18} />{error}
          </motion.div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 font-sans">Email Address</label>
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
              placeholder="admin@example.com" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 font-sans">Password</label>
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
              placeholder="••••••••" required />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 disabled:bg-blue-300 transition-all flex items-center justify-center gap-2 font-sans">
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      <button onClick={onForgotPassword}
        className="w-full mt-6 text-sm text-slate-400 hover:text-blue-500 font-medium transition-colors flex items-center justify-center gap-2 font-sans">
        <KeyRound size={15} /> Forgot your password?
      </button>
    </>
  );
}

// Step 2: Request OTP via WhatsApp
function RequestOTPStep({ onNext, onBack }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {});
      const otp = res.data.otp;
      
      // WhatsApp redirection message
      const whatsappMsg = `Hi VHFMS Admin, your one-time verification code is: *${otp}*. Do not share this with anyone.`;
      const whatsappUrl = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(whatsappMsg)}`;
      
      // Open in new tab/window to "send" the message
      window.open(whatsappUrl, '_blank');
      
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center py-2">
        <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <MessageCircle size={32} className="text-green-500" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2 font-['Oswald']">Mobile Verification</h3>
        <p className="text-sm text-slate-500 leading-relaxed font-sans px-2">
          Click below to receive an OTP on your registered WhatsApp number: <span className="font-bold text-slate-800">...{contactInfo.phone.slice(-4)}</span>
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm font-sans">
          <AlertCircle size={16} />{error}
        </div>
      )}

      {otpSent ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-100 rounded-2xl p-5 text-center">
            <CheckCircle2 size={24} className="text-green-500 mx-auto mb-2" />
            <p className="text-sm text-green-700 font-bold font-sans">OTP sent to your WhatsApp!</p>
            <p className="text-[11px] text-green-600 mt-1 font-sans">Please check your WhatsApp, then return here to enter the code.</p>
          </div>
          <button onClick={onNext}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold shadow-lg shadow-blue-100 transition-all font-sans">
            Continue to Verification →
          </button>
          <button onClick={handleRequest} className="w-full text-xs text-slate-400 hover:text-blue-500 underline underline-offset-4 font-sans">
            Resend OTP code
          </button>
        </div>
      ) : (
        <button onClick={handleRequest} disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white py-4 rounded-xl font-bold shadow-lg shadow-green-100 transition-all flex items-center justify-center gap-2 font-sans">
          {loading ? 'Generating...' : <><MessageCircle size={20} /> Send OTP to my WhatsApp</>}
        </button>
      )}

      <button onClick={onBack} className="w-full text-sm text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1.5 mt-2 font-sans font-medium">
        <ArrowLeft size={14} /> Back to Login
      </button>
    </div>
  );
}

// Step 3: Verify OTP & Reset Password
function ResetPasswordStep({ onBack, onSuccess }) {
  const [enteredOtp, setEnteredOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (enteredOtp.length < 6) {
      setError('Please enter the 6-digit OTP code.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { otp: enteredOtp, new_password: newPassword });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleReset} className="space-y-5">
      <div className="text-center py-2">
        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Lock size={32} className="text-blue-500" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-1 font-['Oswald']">Verify OTP</h3>
        <p className="text-sm text-slate-500 leading-relaxed font-sans px-4">
          Enter the code you received on WhatsApp below.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm font-sans">
          <AlertCircle size={16} />{error}
        </div>
      )}

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1 font-sans">Verification Code</label>
        <input type="text" maxLength={6} value={enteredOtp} onChange={(e) => setEnteredOtp(e.target.value.replace(/\D/g, ''))}
          className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-4xl font-black tracking-[0.5em] font-mono text-blue-600"
          placeholder="000000" required />
      </div>

      <div className="space-y-4 pt-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 font-sans">New Password</label>
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type={showPass ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
              placeholder="Min. 6 characters" required />
            <button type="button" onClick={() => setShowPass(!showPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2 font-sans">Confirm New Password</label>
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type={showPass ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
              placeholder="Confirm new password" required />
          </div>
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 disabled:bg-blue-300 transition-all flex items-center justify-center gap-2 mt-2 font-sans">
        {loading ? 'Updating Password...' : 'Update Password'}
      </button>

      <button type="button" onClick={onBack} className="w-full text-sm text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1.5 font-sans font-medium">
        <ArrowLeft size={14} /> Back
      </button>
    </form>
  );
}

// Step 4: Success
function SuccessStep({ onLogin }) {
  return (
    <div className="text-center py-6 space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto shadow-md">
        <CheckCircle2 size={40} className="text-green-500" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-slate-800 mb-2 font-['Oswald']">Reset Successful!</h3>
        <p className="text-sm text-slate-500 font-sans">Your password has been updated. You can now log in.</p>
      </div>
      <button onClick={onLogin} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100 font-sans">
        <RefreshCw size={18} /> Go to Login
      </button>
    </div>
  );
}

// Main AdminLogin Component
export default function AdminLogin() {
  const [step, setStep] = useState('login');

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 font-sans">
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-blue-200">
            <span className="text-white font-bold text-2xl font-['Oswald']">A</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 font-['Oswald'] tracking-tight">{contactInfo.brandName}</h1>
          <p className="text-slate-400 text-sm mt-1 uppercase tracking-widest font-bold opacity-80 font-sans">Admin Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 border border-slate-100 overflow-hidden relative">
          <AnimatePresence mode="wait">
            {step === 'login' && (
              <motion.div key="login" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                <LoginForm onForgotPassword={() => setStep('request-otp')} />
              </motion.div>
            )}
            {step === 'request-otp' && (
              <motion.div key="request-otp" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <RequestOTPStep
                  onNext={() => setStep('reset')}
                  onBack={() => setStep('login')}
                />
              </motion.div>
            )}
            {step === 'reset' && (
              <motion.div key="reset" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <ResetPasswordStep
                  onBack={() => setStep('request-otp')}
                  onSuccess={() => setStep('success')}
                />
              </motion.div>
            )}
            {step === 'success' && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <SuccessStep onLogin={() => setStep('login')} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-center text-slate-400 text-[10px] mt-10 font-bold uppercase tracking-widest font-sans">
          Secured by ApexNuera Technologies
        </p>
      </motion.div>
    </div>
  );
}
